<template lang="pug">
tr.table-row(:data-test-id="`deal-product-table-row-${dealProduct.Key}`")
  td.order
    input(type="hidden" :value="dealProduct.ProductKey")
    div
      span.handle.cursor-move(v-show="!forEditProduct")
        BriqIcon(name="icon-drag-drop")
      span.program-order {{ dealProduct.ProgramOrder }}
      input(type="hidden" :value="dealProduct.ProgramOrder" readonly)
  td.product-name
    div(style="display: flex; align-items: center;")
      a(
        :href="`/products/${dealProduct.ProductKey}/edit`" 
        target="_blank" 
        rel="noopener noreferrer"
        :title="dealProduct.ProductNameInternal"
        ) {{ dealProduct.ProductNameInternal }}
  td.product-type
    input(type="radio" :value="DealProductType.DealProduct" v-model="dealProductType" :class="{ pending }" :disabled="pending || forEditProduct" data-test-id="radio-product-type-deal")
  td.product-type
    input(type="radio" :value="DealProductType.CrossSell" v-model="dealProductType" :class="{ pending }" :disabled="pending || forEditProduct" data-test-id="radio-product-type-cross-sell")
  td.quantity-pp
    div(v-if="dealProduct.ProductNeedsToBeScheduled || forEditProduct || dealProductType === 'CrossSell'") {{ dealProduct.ProductQuantity }}
    .flex(v-else)
      .flex-1-1
        input.form-element(
          type="number"
          min="1"
          step="1"
          placeholder="0"
          v-model.number="dealProduct.ProductQuantity"
          :disabled="pending"
          :class="{ pending }"
          data-test-id="product-quantity"
          )
  td.price-type {{ displayPriceType(dealProduct.PriceType) }}
    input(type="hidden" :value="dealProduct.PriceType")
  td.base-price.less-padding-cell {{ basePrice }}
  td.price-change
    //v-model="dealProduct.PriceOffset"
    BriqPriceInput(
      data-test-id="price-offset-input"
      v-if="canManipulatePrice"
      :value="dealProduct.PriceOffset"
      @input="v => $emit('dealProductPriceOffsetInput', { dealProductIndex: productIndex, dealProductKey: dealProduct.Key, priceOffset: v || 0 })"
      :placeholder="0"
      :currencySymbol="currencySymbol"
      :step="1"
      allowNegativeValues
      @blur="roundPriceOffset"
      :pending="pending"
      )
    span(v-else) {{ priceChange }}
  td.remove-action.less-padding-cell(v-if="!forEditProduct")
    .product-actions
      button.product-action(
        type="button"
        @click="remove"
        :data-test-id="`btn-remove-product-${dealProduct.Key}`"
        :class="{ pending }"
        :disabled="pending"
        )
        BriqIcon(name="icon-input-trash-icon")
</template>

<script>
import { mapGetters } from 'vuex'
import { pipe, replace, toUpper } from 'ramda'
import { currencySymbol } from '@/helpers/intlFormatter.js'
import productPropValidator from './productPropValidator.js'
import BriqPriceInput from '@/vue/components/BriqPriceInput.vue'
import DealProductType from '@v/bookings/edit/models/DealProductType.js'

// todo: disable inputs while saving

export default {
  name: 'DealProductsTableRow',
  components: { BriqPriceInput },
  props: {
    dealProduct: { type: Object, required: true, validator: productPropValidator },
    forEditProduct: Boolean,
    editProductKey: String,
    productIndex: Number,
    pending: Boolean,
  },
  data() {
    return {
      shortStyle: 'min-width: 100px',
      DealProductType,
    }
  },
  computed: {
    ...mapGetters('briqState', ['envFeatureFlags']),
    dealProductType: {
      get() {
        return this.dealProduct.Type
      },
      set(v) {
        this.dealProduct.ProductQuantity = 1
        this.dealProduct.Type = v
      },
    },
    enableEditUpsellPrice() {
      return this.envFeatureFlags['enable-edit-upsell-price']
    },
    currencySymbol() {
      return currencySymbol(this.$locale, this.$currency)
    },
    basePrice() {
      return this.$formatCurrency(this.dealProduct?.BasePrice)
    },
    priceChange() {
      return this.$formatCurrency(this.dealProduct?.PriceOffset)
    },
    canManipulatePrice() {
      const productMatchesDealProduct = this.editProductKey === this.dealProduct.ProductKey
      const isTypeCrossSell = this.dealProduct.Type === DealProductType.CrossSell

      if (this.forEditProduct) {
        if (productMatchesDealProduct && isTypeCrossSell && this.enableEditUpsellPrice) return true
        if (productMatchesDealProduct && !isTypeCrossSell) return true
        return false
      } else {
        if (isTypeCrossSell && this.enableEditUpsellPrice) return true
        if (!isTypeCrossSell) return true
        return false
      }
    },
  },
  methods: {
    displayPriceType: pipe(replace(/([A-Z])/g, ' $1'), replace(/^./, toUpper)),
    remove() {
      this.$emit('remove', this.productIndex)
    },
    roundPriceOffset() {
      this.dealProduct.PriceOffset = Math.round(this.dealProduct.PriceOffset * 100) / 100
    },
  },
  watch: {
    dealProductType(newVal) {
      if (newVal === DealProductType.CrossSell) this.dealProduct.PriceOffset = 0
    },
  },
}
</script>

<style scoped lang="stylus">
// todo: use the new dimensions from variables

.flex-1-1
  flex 1 1 50%

td.less-padding-cell
  padding 8px 10px

span.handle.cursor-move
  margin-right 0.3em

svg.icon.icon-drag-drop
  vertical-align middle

.product-actions
  display flex
  justify-content space-evenly

  :before
    width 0px

  button
    .icon
      fill #4e5a6a

    &:hover
      .icon
        fill #2c333a

    &:disabled
      opacity 0.5

    &.pending
      cursor wait

.product-action
  padding 0 14px 0 0

  &:last-child
    padding-right 0

.price-change
  padding 8px 12px

.product-name
  max-width 155px

  a
    text-decoration underline
    overflow hidden
    text-overflow ellipsis
    white-space nowrap
    display inline-block
    width 100%

.product-type
  text-align center

  input
    cursor pointer
    display flex
    width 100%
    align-items center

    &:disabled
      cursor not-allowed

    &.pending
      cursor wait

.table-row
  line-height 40px
</style>
